<script setup>
import { defineProps, useSlots, ref, computed, watch } from 'vue'
import { injectDocExpansion } from '@/composables/useDocExpansion'

const props = defineProps({
  name: String,
  type: String,
  description: String,
  depth: { type: Number, default: 0 }
})

const slots = useSlots()
const hasChildren = computed(() => slots.default && slots.default().length > 0)
const isExpanded = ref(props.depth === 0)

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

// Listen for master expand/collapse events
const docExpansion = injectDocExpansion()
if (docExpansion) {
  watch(() => docExpansion.expandAll.value, (newValue) => {
    if (newValue && hasChildren.value) {
      isExpanded.value = newValue.state
    }
  })
}
</script>

<template>
  <div class="attribute-wrapper">
    <div class="attribute-header"
         :class="{ 'clickable': hasChildren }"
         @click="hasChildren && toggleExpanded()">
      <div class="d-flex justify-content-between align-items-start">
        <div class="flex-grow-1">
          <div class="d-flex align-items-center mb-2">
            <span class="me-2 attribute-name">{{ props.name }}</span>
            <span v-if="props.type == 'string'" class="badge text-bg-primary">{{ props.type }}</span>
            <span v-else-if="props.type == 'integer'" class="badge text-bg-warning" style="color: white;">{{ props.type }}</span>
            <span v-else-if="props.type == 'float'" class="badge text-bg-secondary" style="color: white;">{{ props.type }}</span>
            <span v-else class="badge text-bg-dark">{{ props.type }}</span>
          </div>
          <p class="attribute-description">{{ props.description }}</p>
        </div>
        <font-awesome-icon v-if="hasChildren"
                           :icon="['fas', isExpanded ? 'caret-down' : 'caret-right']"
                           class="caret-icon" />
      </div>
    </div>
    <Transition name="expand">
      <div v-if="!hasChildren || isExpanded" class="attribute-children">
        <slot></slot>
      </div>
    </Transition>
  </div>
</template>

<style scoped>

.form-control {
  padding: 1rem;
  border-radius: 10px;
  border:1px solid #313946;
}

label {
  padding:1rem;
  font-weight: normal;
}

.form-floating > .form-control:focus, .form-floating > .form-control:not(:placeholder-shown) {
  padding-top: 1.2rem;
  padding-bottom: 0;
}

.attribute-wrapper {
  margin-bottom: 0.75rem;
}

.attribute-header {
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}

.attribute-header.clickable {
  cursor: pointer;
}

.attribute-header.clickable:hover {
  background-color: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
}

.attribute-name {
  color: white;
  font-size: 1.05rem;
  font-weight: 500;
}

.attribute-description {
  color: #9ca3af;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
}

.attribute-children {
  padding-left: 1.5rem;
  margin-top: 0.75rem;
}

.caret-icon {
  color: #8b92a0;
  transition: all 0.3s ease;
  font-size: 1.3rem;
  margin-left: 1rem;
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.attribute-header.clickable:hover .caret-icon {
  color: #a8aeb9;
}

.expand-enter-active, .expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from, .expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to, .expand-leave-from {
  opacity: 1;
  max-height: 2000px;
}

</style>
